import { get, push, ref, update } from "firebase/database";

export const CRON_PRODUCT_IMAGE_SUMMARY_ROOT = "cronProductImageConfigSummaries";
export const PRODUCT_IMAGE_PLATFORM_FACEBOOK = "facebook";
export const PRODUCT_IMAGE_PLATFORM_ZALO = "zalo";
export const PRODUCT_IMAGE_SUMMARY_VERSION = 1;
export const PRODUCT_IMAGE_SUMMARY_PLATFORMS = [
  PRODUCT_IMAGE_PLATFORM_FACEBOOK,
  PRODUCT_IMAGE_PLATFORM_ZALO,
];

const normalizeBoolean = (value) => value === true;

export const normalizeProductImagePlatform = (config = {}) =>
  String(config?.platform || "").toLowerCase() === PRODUCT_IMAGE_PLATFORM_ZALO
    ? PRODUCT_IMAGE_PLATFORM_ZALO
    : PRODUCT_IMAGE_PLATFORM_FACEBOOK;

const getOtherPlatforms = (platform) =>
  PRODUCT_IMAGE_SUMMARY_PLATFORMS.filter((item) => item !== platform);

export const buildProductImagePlatformEnabledKey = (config = {}) => {
  const platform = normalizeProductImagePlatform(config);
  return `${platform}:${normalizeBoolean(config?.autoPostEnabled)}`;
};

export const buildProductImageCronSummaryId = (userId, productId) =>
  `${encodeURIComponent(String(userId))}__${encodeURIComponent(
    String(productId)
  )}`;

const normalizeCronEligible = (options = {}) =>
  typeof options.cronEligible === "boolean" ? options.cronEligible : true;

export const buildProductImageCronSummary = (
  userId,
  productId,
  config = {},
  options = {}
) => {
  const platform = normalizeProductImagePlatform(config);
  const autoPostEnabled = normalizeBoolean(config?.autoPostEnabled);
  const cronEligible = normalizeCronEligible(options);
  const summary = {
    userId,
    productId,
    platform,
    autoPostEnabled,
    platformEnabledKey: `${platform}:${autoPostEnabled}`,
    postTime: config?.postTime ?? null,
    postTimes: Array.isArray(config?.postTimes) ? config.postTimes : null,
    selectedDays:
      config?.selectedDays && typeof config.selectedDays === "object"
        ? config.selectedDays
        : null,
    scheduleMode: config?.scheduleMode || "weekly",
    fixedDateSchedules: Array.isArray(config?.fixedDateSchedules)
      ? config.fixedDateSchedules
      : null,
    updatedAt: config?.updatedAt || Date.now(),
    summaryVersion: PRODUCT_IMAGE_SUMMARY_VERSION,
  };

  summary.cronEligible = cronEligible;
  summary.platformEnabledEligibleKey = `${platform}:${autoPostEnabled}:${cronEligible}`;

  return summary;
};

export const buildProductImageCronIndexUpdates = (
  userId,
  productId,
  config = {},
  options = {}
) => {
  const platform = normalizeProductImagePlatform(config);
  const summaryId = buildProductImageCronSummaryId(userId, productId);
  const summary = buildProductImageCronSummary(userId, productId, config, options);
  const updates = {
    [`productImages/${userId}/${productId}/platformEnabledKey`]:
      summary.platformEnabledKey,
    [`${CRON_PRODUCT_IMAGE_SUMMARY_ROOT}/${platform}/${summaryId}`]: summary,
  };

  getOtherPlatforms(platform).forEach((otherPlatform) => {
    updates[
      `${CRON_PRODUCT_IMAGE_SUMMARY_ROOT}/${otherPlatform}/${summaryId}`
    ] = null;
  });

  return updates;
};

export const buildProductImageCronIndexDeleteUpdates = (userId, productId) => {
  const summaryId = buildProductImageCronSummaryId(userId, productId);

  return {
    [`${CRON_PRODUCT_IMAGE_SUMMARY_ROOT}/${PRODUCT_IMAGE_PLATFORM_FACEBOOK}/${summaryId}`]:
      null,
    [`${CRON_PRODUCT_IMAGE_SUMMARY_ROOT}/${PRODUCT_IMAGE_PLATFORM_ZALO}/${summaryId}`]:
      null,
  };
};

export const buildProductImageConfigUpsertUpdates = (
  userId,
  productId,
  config = {},
  options = {}
) => {
  const updates = {};

  Object.entries(config).forEach(([key, value]) => {
    if (value !== undefined) {
      updates[`productImages/${userId}/${productId}/${key}`] = value;
    }
  });

  return {
    ...updates,
    ...buildProductImageCronIndexUpdates(userId, productId, config, options),
  };
};

const getExistingProductImageCronEligibility = async (
  database,
  userId,
  productId
) => {
  const summaryId = buildProductImageCronSummaryId(userId, productId);
  const snapshots = await Promise.all(
    PRODUCT_IMAGE_SUMMARY_PLATFORMS.map((platform) =>
      get(
        ref(database, `${CRON_PRODUCT_IMAGE_SUMMARY_ROOT}/${platform}/${summaryId}`)
      )
        .then((snapshot) => (snapshot?.exists() ? snapshot.val() : null))
        .catch(() => null)
    )
  );
  const summary = snapshots.find(
    (item) => item && typeof item.cronEligible === "boolean"
  );
  return typeof summary?.cronEligible === "boolean"
    ? summary.cronEligible
    : undefined;
};

export const saveProductImageConfigWithCronIndex = async (
  database,
  userId,
  productId,
  config = {}
) => {
  if (productId) {
    const cronEligible = await getExistingProductImageCronEligibility(
      database,
      userId,
      productId
    );
    await update(
      ref(database),
      buildProductImageConfigUpsertUpdates(userId, productId, config, {
        cronEligible,
      })
    );
    return { key: productId };
  }

  const productRef = push(ref(database, `productImages/${userId}`));
  await update(
    ref(database),
    buildProductImageConfigUpsertUpdates(userId, productRef.key, {
      ...config,
      id: productRef.key,
    })
  );
  return productRef;
};

export const removeProductImageConfigWithCronIndex = async (
  database,
  userId,
  productId
) =>
  update(ref(database), {
    [`productImages/${userId}/${productId}`]: null,
    ...buildProductImageCronIndexDeleteUpdates(userId, productId),
  });
